import React from 'react';
import {
  Box,
  Text,
  Icon,
  CloseButton,
  Flex,
  useToast,
  Button,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaInfoCircle, 
  FaTimesCircle
} from 'react-icons/fa';

const MotionBox = motion(Box);

interface ModernToastProps {
  title?: string;
  description?: string;
  status?: 'success' | 'error' | 'warning' | 'info';
  onClose?: () => void;
  isClosable?: boolean;
  ctaLink?: string;
}

interface ToastRenderProps {
  onClose: () => void;
}

const getStatusConfig = (status: string) => {
  switch (status) {
    case 'success':
      return {
        icon: FaCheckCircle,
        bg: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
        borderColor: '#68D391',
        iconColor: 'white',
      };
    case 'error':
      return {
        icon: FaTimesCircle,
        bg: 'linear-gradient(135deg, #F56565 0%, #E53E3E 100%)',
        borderColor: '#FC8181',
        iconColor: 'white',
      };
    case 'warning':
      return {
        icon: FaExclamationTriangle,
        bg: 'linear-gradient(135deg, #ED8936 0%, #DD6B20 100%)',
        borderColor: '#F6AD55',
        iconColor: 'white',
      };
    case 'info':
      return {
        icon: FaInfoCircle,
        bg: 'linear-gradient(135deg, #4299E1 0%, #3182CE 100%)',
        borderColor: '#63B3ED',
        iconColor: 'white',
      };
    default:
      return {
        icon: FaInfoCircle,
        bg: 'linear-gradient(135deg, #4A5568 0%, #2D3748 100%)',
        borderColor: '#718096',
        iconColor: 'white',
      };
  }
};

export const ModernToast: React.FC<ModernToastProps> = ({
  title,
  description,
  status = 'info',
  onClose,
  isClosable = true,
  ctaLink,
}) => {
  const config = getStatusConfig(status);
  const IconComponent = config.icon;

  return (
    <MotionBox
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 50, scale: 0.9 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      bg={config.bg}
      borderRadius="xl"
      boxShadow="0 10px 40px rgba(0, 0, 0, 0.3)"
      border="1px solid"
      borderColor={config.borderColor}
      backdropFilter="blur(10px)"
      p={4}
      minW="320px"
      maxW="400px"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        bg: 'whiteAlpha.300',
      }}
    >
      <Flex align="flex-start" gap={3}>
        <Icon
          as={IconComponent}
          color={config.iconColor}
          fontSize="20px"
          mt="2px"
          flexShrink={0}
        />
        
        <Box flex="1" minW={0}>
          {title && (
            <Text
              fontWeight="600"
              fontSize="md"
              color="white"
              mb={description ? 1 : 0}
              lineHeight="1.3"
            >
              {title}
            </Text>
          )}
          {description && (
            <Text
              color="whiteAlpha.900"
              fontSize="sm"
              lineHeight="1.4"
              mb={ctaLink ? 3 : 0}
            >
              {description}
            </Text>
          )}
          {ctaLink && (
            <Flex justify="center" mt={2}>
              <Button
                size="sm"
                variant="outline"
                color="white"
                borderColor="whiteAlpha.300"
                _hover={{
                  bg: 'whiteAlpha.200',
                  borderColor: 'whiteAlpha.500',
                }}
                _active={{
                  bg: 'whiteAlpha.300',
                }}
                onClick={() => window.open(ctaLink, '_blank')}
                fontSize="xs"
                fontWeight="500"
                px={3}
                py={1}
                h="auto"
              >
                View
              </Button>
            </Flex>
          )}
        </Box>

        {isClosable && onClose && (
          <CloseButton
            size="sm"
            color="whiteAlpha.700"
            _hover={{
              color: 'white',
              bg: 'whiteAlpha.200',
            }}
            onClick={onClose}
            flexShrink={0}
          />
        )}
      </Flex>
    </MotionBox>
  );
};

// Custom hook for using the modern toast
export const useModernToast = () => {
  const toast = useToast();
  
  return {
    success: (title: string, description?: string, ctaLink?: string) =>
      toast({
        render: ({ onClose }: ToastRenderProps) => (
          <ModernToast
            title={title}
            description={description}
            status="success"
            onClose={onClose}
            ctaLink={ctaLink}
          />
        ),
      }),
    error: (title: string, description?: string, ctaLink?: string) =>
      toast({
        render: ({ onClose }: ToastRenderProps) => (
          <ModernToast
            title={title}
            description={description}
            status="error"
            onClose={onClose}
            ctaLink={ctaLink}
          />
        ),
      }),
    warning: (title: string, description?: string, ctaLink?: string) =>
      toast({
        render: ({ onClose }: ToastRenderProps) => (
          <ModernToast
            title={title}
            description={description}
            status="warning"
            onClose={onClose}
            ctaLink={ctaLink}
          />
        ),
      }),
    info: (title: string, description?: string, ctaLink?: string) =>
      toast({
        render: ({ onClose }: ToastRenderProps) => (
          <ModernToast
            title={title}
            description={description}
            status="info"
            onClose={onClose}
            ctaLink={ctaLink}
          />
        ),
      }),
  };
}; 